var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as Sentry from "@sentry/browser";
import { setZoomTrackingContext, umamiTrack, emitSearchResultEngagement, setSearchFilterContext } from "./index";
import { compact } from "./analytics";
// Matches urls.php's own hardcoded category_group/category route regexes.
var CATEGORY_GROUPS = ['brooches', 'earrings', 'necklaces', 'misc'];
// Index order matches view-style-range's value and .view-style-labels'
// data-value in search.html.
var VIEW_STYLES = ['list', 'grid-small', 'grid-large'];
var VIEW_STYLE_STORAGE_KEY = 'pbex-search-view-style';
/**
 * Drives /{lang}/search after its first, server-rendered paint: every
 * filter/sort change re-fetches /{lang}/api/search-results and swaps the
 * grid + facet counts in place, keeping the URL in sync via pushState so
 * results stay bookmarkable/shareable and back/forward works. Also runs on
 * category.html/sub-category.html (CategoryGroupDocument/
 * ItemCategoryDocument), which render this same filter bar/grid preselected
 * to one group/category at their own dedicated URL -- paramsFromCurrentLocation()
 * and canonicalUrlPath() below translate between that URL shape and the
 * plain /{lang}/search?group[]=.../category[]=... query-string shape
 * everything else here works with, but only apply on those pages (see
 * startedOnCategoryPage) -- picking the same filters from plain /search
 * only ever narrows results/updates the URL in that plain query-string
 * shape, never navigating to a category/sub-category page's own URL.
 * See docs/superpowers/specs/2026-08-21-site-search-design.md.
 */
export function runSearchResults(initial) {
    var _a, _b, _c;
    // Every item card's zoom lightbox (Fancybox.bind() in index.ts, delegated
    // so it also covers AJAX-appended cards) fires the same "View Item"
    // tracking a category page's own zoom does -- attribute it to "search"
    // rather than leaving it unset.
    setZoomTrackingContext('search', 'search');
    // This function also runs on category.html/sub-category.html
    // (CategoryGroupDocument/ItemCategoryDocument), whose own pathname
    // (/{lang}/{group}[/{category}]) doesn't end in "/search" -- the AJAX
    // endpoint is always /{lang}/api/search-results regardless of which
    // page's own URL this is, so derive it from the language prefix alone,
    // not a "/search" suffix replacement.
    var lang = (_b = (_a = window.location.pathname.match(/^\/([a-z]{2})(\/|$)/)) === null || _a === void 0 ? void 0 : _a[1]) !== null && _b !== void 0 ? _b : 'en';
    var searchApiUrl = "/".concat(lang, "/api/search-results");
    // Where this page STARTED -- captured once from the initial pathname,
    // never re-derived, so client-side filtering that rewrites the URL
    // doesn't retroactively relabel a plain /search session as a category
    // one (or vice-versa). Invoked below, once startedOnCategoryPage exists.
    function detectContext(onCategoryPage) {
        if (!onCategoryPage) {
            return { context: 'search' };
        }
        var segments = window.location.pathname.split('/').filter(function (s) { return s !== ''; });
        if (segments.length >= 3 && CATEGORY_GROUPS.indexOf(segments[1]) !== -1) {
            return { context: 'subcategory', pageGroup: segments[1] };
        }
        return { context: 'category', pageGroup: segments[1] };
    }
    function emitSearch(query, results, source) {
        umamiTrack('Search', { query: query, results: results, context: searchContext, source: source });
        gtag('event', 'search', { search_term: query });
        if (results === 0) {
            gtag('event', 'no_search_results', { search_term: query });
        }
    }
    var grid = document.getElementById('search-result-grid');
    var resultCountEl = document.getElementById('search-result-count');
    var loadMoreContainer = document.getElementById('search-load-more-container');
    var loadMoreButton = document.getElementById('search-load-more');
    var resultsSection = document.getElementById('search-results');
    var categoryBrowse = document.getElementById('search-category-browse');
    var categoryBrowseGroups = Array.from(document.querySelectorAll('.search-subcategory-browse'));
    var filtersRoot = document.getElementById('filter-bar');
    var resetButton = document.getElementById('filter-reset');
    var sortSelect = document.getElementById('search-sort');
    var soldOutToggle = document.getElementById('sold-out-toggle');
    // Captured once, before any shopper interaction -- this page's own
    // server-resolved state (false unless the URL already carries
    // include_sold_out[]=1), used by
    // syncFiltersFromParams() below to restore the *right* state on a
    // browser-back to this page's own original, pre-interaction URL (which
    // never carries an explicit include_sold_out param, the same way the
    // very first server render doesn't either).
    var soldOutDefaultChecked = (_c = soldOutToggle === null || soldOutToggle === void 0 ? void 0 : soldOutToggle.checked) !== null && _c !== void 0 ? _c : false;
    var viewStyleRange = document.getElementById('view-style-range');
    var viewStyleLabels = Array.from(document.querySelectorAll('.view-style-labels span'));
    // #page-intro-title (category.html/sub-category.html's own heading, see
    // search.html) only exists when the page was *server-rendered* as one
    // of those, never injected later -- read once here (not per update, see
    // updatePageChrome()) to tell "started on a category/sub-category page"
    // apart from "started on plain /search and filtered down to one",
    // which shouldn't retitle the tab into looking like a real navigation
    // to that category page when no such navigation actually happened.
    var startedOnCategoryPage = document.getElementById('page-intro-title') !== null;
    var _d = detectContext(startedOnCategoryPage), searchContext = _d.context, pageGroup = _d.pageGroup;
    // .search-results-page only ever has this one .search-box (the header
    // nav's own search link goes straight to a plain, filter-free
    // /{lang}/search URL -- see base.html -- not a form of its own), but
    // scoping the lookup here anyway keeps this file's own state tied to
    // this page's "Refine search" box specifically.
    var searchForm = document.querySelector('.search-results-page .search-box');
    var qInput = searchForm === null || searchForm === void 0 ? void 0 : searchForm.querySelector('input[name=q]');
    var offset = initial.products.length;
    var total = initial.total;
    if (initial.query !== '') {
        emitSearch(initial.query, initial.total, 'load');
    }
    /**
     * One dual-thumb range slider + a pair of manual number inputs sharing
     * the same min/max state -- Price and Size are both instances of this
     * (see search.html's own .price-slider/.size-slider, each just its own
     * id prefix). Dragging a thumb and typing a number both end up setting
     * the same underlying <input type="range"> value, so every other piece
     * of this file (buildParams(), syncFiltersFromParams(), the browse-
     * state/chip-count/reset-visibility checks, ...) only ever needs to
     * read *that*, never caring which of the two a shopper actually used.
     */
    function createRangeFilter(idPrefix, paramMin, paramMax) {
        var minRange = document.getElementById("".concat(idPrefix, "-min-range"));
        var maxRange = document.getElementById("".concat(idPrefix, "-max-range"));
        var minInputEl = document.getElementById("".concat(idPrefix, "-min-input"));
        var maxInputEl = document.getElementById("".concat(idPrefix, "-max-input"));
        var track = document.querySelector("#".concat(idPrefix, "-slider .range-slider-track"));
        function isActive() {
            return !!(minRange && maxRange && (minRange.value !== minRange.min || maxRange.value !== maxRange.max));
        }
        // Keeps the manual inputs and the track's ::before "selected range"
        // gradient (see style.css) in sync with wherever minRange/maxRange's
        // own values currently stand -- called after every drag, manual
        // edit, reset, or popstate sync, regardless of which one changed
        // them.
        function updateVisuals() {
            if (!minRange || !maxRange) {
                return;
            }
            if (minInputEl) {
                minInputEl.value = minRange.value;
            }
            if (maxInputEl) {
                maxInputEl.value = maxRange.value;
            }
            if (track) {
                var boundsMin = parseFloat(minRange.min);
                var boundsMax = parseFloat(minRange.max);
                var span = boundsMax - boundsMin || 1;
                var minPercent = ((parseFloat(minRange.value) - boundsMin) / span) * 100;
                var maxPercent = ((parseFloat(maxRange.value) - boundsMin) / span) * 100;
                track.style.setProperty('--min-percent', "".concat(minPercent, "%"));
                track.style.setProperty('--max-percent', "".concat(maxPercent, "%"));
            }
        }
        function reset() {
            if (minRange) {
                minRange.value = minRange.min;
            }
            if (maxRange) {
                maxRange.value = maxRange.max;
            }
            updateVisuals();
        }
        function applyToParams(params) {
            if (minRange && minRange.value !== minRange.min) {
                params.set(paramMin, minRange.value);
            }
            if (maxRange && maxRange.value !== maxRange.max) {
                params.set(paramMax, maxRange.value);
            }
        }
        function syncFromParams(params) {
            var _a, _b;
            if (minRange) {
                minRange.value = (_a = params.get(paramMin)) !== null && _a !== void 0 ? _a : minRange.min;
            }
            if (maxRange) {
                maxRange.value = (_b = params.get(paramMax)) !== null && _b !== void 0 ? _b : maxRange.max;
            }
            updateVisuals();
        }
        if (minRange && maxRange) {
            // Two separate handlers, not one shared one: each clamps the
            // *other* handle to itself when crossed, so dragging past the
            // counterpart moves the counterpart to meet it, regardless of
            // which handle is being dragged. A single shared handler that
            // always writes maxRange.value would only handle "min dragged
            // above max" -- dragging max below min would just snap max back up
            // instead of pulling min down, making it impossible to drag max
            // below the current min.
            minRange.addEventListener('input', function () {
                if (parseFloat(minRange.value) > parseFloat(maxRange.value)) {
                    maxRange.value = minRange.value;
                }
                updateVisuals();
                updateChipCounts();
            });
            // 'input' fires continuously while dragging (live visuals update +
            // the crossing clamp) -- the AJAX-triggering 'change' (fires once,
            // on handle release) is already wired by filtersRoot's delegated
            // listener below; do NOT add a second 'change' listener here, it
            // would double-fire fetchAndRender().
            maxRange.addEventListener('input', function () {
                if (parseFloat(maxRange.value) < parseFloat(minRange.value)) {
                    minRange.value = maxRange.value;
                }
                updateVisuals();
                updateChipCounts();
            });
            updateVisuals();
        }
        // Typing a value and tabbing/clicking away (the number input's own
        // 'change') sets the matching slider handle -- clamped to the
        // slider's own bounds and to not cross the other handle, same rules
        // dragging already follows -- then fetches, same as releasing a
        // dragged handle does.
        function wireManualInput(input, range, otherRange, isMin) {
            if (!input || !range) {
                return;
            }
            input.addEventListener('change', function () {
                var value = parseFloat(input.value);
                if (Number.isNaN(value)) {
                    value = parseFloat(range.value);
                }
                value = Math.min(Math.max(value, parseFloat(range.min)), parseFloat(range.max));
                if (otherRange) {
                    var otherValue = parseFloat(otherRange.value);
                    value = isMin ? Math.min(value, otherValue) : Math.max(value, otherValue);
                }
                range.value = String(value);
                updateVisuals();
                // The fetch + analytics Filter Change are triggered by this change
                // event bubbling to #filter-bar's delegated handler -- do not fetch
                // again here (it would double-fire).
            });
        }
        wireManualInput(minInputEl, minRange, maxRange, true);
        wireManualInput(maxInputEl, maxRange, minRange, false);
        function currentValue() {
            return minRange && maxRange ? "".concat(minRange.value, "-").concat(maxRange.value) : '';
        }
        return { dimension: idPrefix, isActive: isActive, reset: reset, updateVisuals: updateVisuals, applyToParams: applyToParams, syncFromParams: syncFromParams, currentValue: currentValue };
    }
    var priceFilter = createRangeFilter('price', 'price_min', 'price_max');
    var sizeFilter = createRangeFilter('size', 'size_min', 'size_max');
    var rangeFilters = [priceFilter, sizeFilter];
    function buildParams() {
        var params = new URLSearchParams();
        var q = qInput === null || qInput === void 0 ? void 0 : qInput.value.trim();
        if (q) {
            params.set('q', q);
        }
        filtersRoot === null || filtersRoot === void 0 ? void 0 : filtersRoot.querySelectorAll('input[type=checkbox]:checked:not(#sold-out-toggle)').forEach(function (checkbox) {
            // PHP only collects repeated query keys into an array when the key
            // ends in "[]" (bare "category=a&category=b" leaves $_GET['category']
            // a scalar, which SearchResultsApi::toStringArray() then discards
            // entirely) -- append the bracketed form even though the checkboxes
            // themselves are named without it.
            params.append("".concat(checkbox.name, "[]"), checkbox.value);
        });
        // Excluded from the generic loop above (it has no name/value for that
        // to read anyway) -- always sent explicitly, checked or not, so the
        // shopper's choice survives a reload or a shared URL unambiguously.
        if (soldOutToggle) {
            params.append('include_sold_out[]', soldOutToggle.checked ? '1' : '0');
        }
        rangeFilters.forEach(function (filter) { return filter.applyToParams(params); });
        if (sortSelect && sortSelect.value !== 'relevance') {
            params.set('sort', sortSelect.value);
        }
        return params;
    }
    /**
     * Restores every filter control to match a URL's query params -- used
     * after browser back/forward (popstate), so the visible checkboxes/
     * select/sliders agree with the grid that was just re-rendered for that
     * URL. Without this, a shopper who navigates back sees the old result
     * set but stale control state, and the *next* interaction's
     * buildParams() would silently reintroduce filters the URL/grid no
     * longer reflect.
     */
    function syncFiltersFromParams(params) {
        var _a, _b;
        if (qInput) {
            qInput.value = (_a = params.get('q')) !== null && _a !== void 0 ? _a : '';
        }
        filtersRoot === null || filtersRoot === void 0 ? void 0 : filtersRoot.querySelectorAll('input[type=checkbox]:not(#sold-out-toggle)').forEach(function (checkbox) {
            checkbox.checked = params.getAll("".concat(checkbox.name, "[]")).indexOf(checkbox.value) !== -1;
        });
        // Mirrors buildParams()'s own special case. Falls back to this page's
        // own original default (not `false`) when the param is absent --
        // reachable on a browser-back all the way to this page's first,
        // pre-interaction history entry, which (like the very first server
        // render) never carries an explicit include_sold_out param at all.
        if (soldOutToggle) {
            soldOutToggle.checked = params.has('include_sold_out[]')
                ? params.getAll('include_sold_out[]').indexOf('1') !== -1
                : soldOutDefaultChecked;
        }
        rangeFilters.forEach(function (filter) { return filter.syncFromParams(params); });
        if (sortSelect) {
            sortSelect.value = (_b = params.get('sort')) !== null && _b !== void 0 ? _b : 'relevance';
        }
        updateSubcategoryVisibility();
    }
    /**
     * product.item_html is already the complete, server-rendered
     * <div class="item-preview">...</div> -- the exact same _item-card.html
     * partial category pages use (see
     * SearchResultsApi::toResponseProduct()), so this just parses that
     * string into a real element instead of building any markup of its
     * own. Returns null only if item_html came back empty (the item's row
     * vanished server-side between the cached snapshot and this request).
     */
    function renderProduct(product) {
        if (!product.item_html) {
            return null;
        }
        var template = document.createElement('template');
        template.innerHTML = product.item_html.trim();
        return template.content.firstElementChild;
    }
    function renderFacets(facets) {
        if (!filtersRoot) {
            return;
        }
        var groups = [
            ['group', facets.groups],
            ['category', facets.categories],
            ['color', facets.colors],
        ];
        groups.forEach(function (_a) {
            var name = _a[0], options = _a[1];
            options.forEach(function (option) {
                var _a, _b, _c;
                var value = (_b = (_a = option.slug) !== null && _a !== void 0 ? _a : option.value) !== null && _b !== void 0 ? _b : '';
                var input = filtersRoot.querySelector("input[name=\"".concat(name, "\"][value=\"").concat(value, "\"]"));
                if (!input) {
                    return;
                }
                var countEl = (_c = input.closest('label')) === null || _c === void 0 ? void 0 : _c.querySelector('.facet-count');
                if (countEl) {
                    countEl.textContent = "(".concat(option.count, ")");
                }
                input.disabled = option.count === 0 && !input.checked;
            });
        });
    }
    /**
     * Scopes the "Sub-category" panel's options to the group(s) currently
     * checked in the "Category" panel (hiding the rest, not removing them --
     * an already-checked sub-category stays checked and still shows up as
     * a removable active-filter chip even while its group is hidden). Shows
     * every sub-category when no Category is selected.
     */
    function updateSubcategoryVisibility() {
        if (!filtersRoot) {
            return;
        }
        var selectedGroups = Array.from(filtersRoot.querySelectorAll('input[name=group]:checked')).map(function (checkbox) { return checkbox.value; });
        filtersRoot.querySelectorAll('.filter-panel-group').forEach(function (el) {
            var _a;
            var group = (_a = el.dataset.group) !== null && _a !== void 0 ? _a : '';
            el.hidden = selectedGroups.length > 0 && selectedGroups.indexOf(group) === -1;
        });
    }
    function updateChipCounts() {
        ['group', 'category', 'color'].forEach(function (dimension) {
            var _a;
            var chipGroup = filtersRoot === null || filtersRoot === void 0 ? void 0 : filtersRoot.querySelector(".filter-chip-group[data-filter-dimension=\"".concat(dimension, "\"]"));
            var count = (_a = filtersRoot === null || filtersRoot === void 0 ? void 0 : filtersRoot.querySelectorAll("input[name=\"".concat(dimension, "\"]:checked")).length) !== null && _a !== void 0 ? _a : 0;
            var countEl = chipGroup === null || chipGroup === void 0 ? void 0 : chipGroup.querySelector('.chip-count');
            if (countEl) {
                countEl.textContent = String(count);
                countEl.hidden = count === 0;
            }
            var panelResetButton = chipGroup === null || chipGroup === void 0 ? void 0 : chipGroup.querySelector('.filter-panel-reset');
            if (panelResetButton) {
                panelResetButton.disabled = count === 0;
            }
        });
        rangeFilters.forEach(function (filter) {
            var countEl = document.getElementById("".concat(filter.dimension, "-chip-count"));
            if (countEl) {
                countEl.hidden = !filter.isActive();
            }
            var panelResetButton = document.querySelector(".filter-chip-group[data-filter-dimension=\"".concat(filter.dimension, "\"] .filter-panel-reset"));
            if (panelResetButton) {
                panelResetButton.disabled = !filter.isActive();
            }
        });
    }
    // Clears every selected value in one filter dimension -- used by each
    // panel's own "Reset" button.
    function resetDimension(dimension) {
        var rangeFilter = rangeFilters.find(function (filter) { return filter.dimension === dimension; });
        if (rangeFilter) {
            rangeFilter.reset();
            applyFilterChange({ dimension: dimension, action: 'clear' });
            return;
        }
        filtersRoot === null || filtersRoot === void 0 ? void 0 : filtersRoot.querySelectorAll("input[name=\"".concat(dimension, "\"]:checked")).forEach(function (checkbox) {
            checkbox.checked = false;
        });
        if (dimension === 'group') {
            updateSubcategoryVisibility();
        }
        applyFilterChange({ dimension: dimension, action: 'clear' });
    }
    // Clears every filter control's own DOM state (checkboxes, price/size
    // ranges) without fetching anything -- shared by the "Reset filters"
    // button and the "Refine search" box's own submit handler below
    // (typing a new query starts a fresh, unfiltered search rather than
    // narrowing whatever filters happened to still be checked). Leaves
    // sort untouched, same as the "Reset filters" button always has.
    function resetAllFilterControls() {
        filtersRoot === null || filtersRoot === void 0 ? void 0 : filtersRoot.querySelectorAll('input[type=checkbox]:checked').forEach(function (checkbox) {
            checkbox.checked = false;
        });
        rangeFilters.forEach(function (filter) { return filter.reset(); });
        updateSubcategoryVisibility();
    }
    function hasAnyActiveFilter() {
        var _a;
        var anyChecked = ((_a = filtersRoot === null || filtersRoot === void 0 ? void 0 : filtersRoot.querySelectorAll('input[type=checkbox]:checked').length) !== null && _a !== void 0 ? _a : 0) > 0;
        return anyChecked || rangeFilters.some(function (filter) { return filter.isActive(); });
    }
    // Set true at the very end of runSearchResults(), after the first
    // render -- gates every analytics emitter so SSR-preset filters and
    // popstate restores never count as deliberate changes.
    var analyticsReady = false;
    function checkedValues(name) {
        var _a;
        return Array.from((_a = filtersRoot === null || filtersRoot === void 0 ? void 0 : filtersRoot.querySelectorAll("input[name=\"".concat(name, "\"]:checked"))) !== null && _a !== void 0 ? _a : []).map(function (checkbox) { return checkbox.value; }).sort();
    }
    function buildFilterSnapshot() {
        var _a, _b, _c;
        var groups = checkedValues('group');
        var categories = checkedValues('category');
        var colors = checkedValues('color');
        var query = (_a = qInput === null || qInput === void 0 ? void 0 : qInput.value.trim()) !== null && _a !== void 0 ? _a : '';
        return {
            context: searchContext,
            page_group: pageGroup,
            has_query: query !== '',
            query_len: query.length,
            groups: groups.join(','),
            group_count: groups.length,
            categories: categories.join(','),
            category_count: categories.length,
            colors: colors.join(','),
            color_count: colors.length,
            price_filtered: priceFilter.isActive(),
            size_filtered: sizeFilter.isActive(),
            sold_out_shown: (_b = soldOutToggle === null || soldOutToggle === void 0 ? void 0 : soldOutToggle.checked) !== null && _b !== void 0 ? _b : false,
            sort: (_c = sortSelect === null || sortSelect === void 0 ? void 0 : sortSelect.value) !== null && _c !== void 0 ? _c : 'relevance',
            result_total: total,
        };
    }
    function emitFilterChange(partial, data) {
        if (!analyticsReady) {
            return;
        }
        var snapshot = buildFilterSnapshot();
        umamiTrack('Filter Change', compact({
            dimension: partial.dimension,
            action: partial.action,
            value: partial.value,
            context: snapshot.context,
            page_group: snapshot.page_group,
            has_query: snapshot.has_query,
            result_total: data.total,
            group_count: snapshot.group_count,
            category_count: snapshot.category_count,
            color_count: snapshot.color_count,
            price_filtered: snapshot.price_filtered,
            size_filtered: snapshot.size_filtered,
            sold_out_shown: snapshot.sold_out_shown,
            sort: snapshot.sort,
        }));
        gtag('event', 'filter_change', {
            filter_dimension: partial.dimension,
            filter_action: partial.action,
        });
    }
    // Every deliberate filter/sort interaction routes through here instead
    // of calling fetchAndRender(false, true) directly, so the resulting
    // event fires only once its AJAX response lands (giving an accurate
    // result_total).
    function applyFilterChange(partial) {
        fetchAndRender(false, true, function (data) { return emitFilterChange(partial, data); });
    }
    function updateResetVisibility() {
        if (resetButton) {
            resetButton.hidden = !hasAnyActiveFilter();
        }
    }
    /**
     * Result grid display density -- purely a sticky per-browser display
     * preference (localStorage), not a search filter, so it's deliberately
     * never read by buildParams()/carried into the URL. Index into
     * VIEW_STYLES matches view-style-range's value and each
     * .view-style-labels span's data-value (see search.html).
     */
    function applyViewStyle(index) {
        var _a;
        var _b;
        var style = (_b = VIEW_STYLES[index]) !== null && _b !== void 0 ? _b : VIEW_STYLES[1];
        (_a = grid.classList).remove.apply(_a, VIEW_STYLES.map(function (s) { return "view-".concat(s); }));
        grid.classList.add("view-".concat(style));
        viewStyleLabels.forEach(function (label) {
            label.classList.toggle('active', label.dataset.value === String(index));
        });
        localStorage.setItem(VIEW_STYLE_STORAGE_KEY, String(index));
    }
    /**
     * Three mutually-exclusive states (sort doesn't count towards any of
     * this -- see SearchResultsDocument's identical rules): nothing
     * searched/filtered at all shows the 4 top-level category tiles;
     * exactly one Category picked and nothing else -- but only on the
     * standalone category-group page (see the startedOnCategoryPage gate
     * below) -- shows that group's sub-category tiles (categoryBrowseGroups'
     * one matching data-group); anything else, plain /search's own single-
     * Category selection included, shows the flat results grid. Re-checked
     * after every non-append render so moving between all three (picking a
     * filter, Reset filters, checking/unchecking the single active
     * Category) works without a page reload, matching search.html's
     * initial server-rendered choice among the three on first paint.
     */
    function updateBrowseState() {
        var _a, _b, _c;
        var q = (_a = qInput === null || qInput === void 0 ? void 0 : qInput.value.trim()) !== null && _a !== void 0 ? _a : '';
        var checkedGroups = Array.from((_b = filtersRoot === null || filtersRoot === void 0 ? void 0 : filtersRoot.querySelectorAll('input[name=group]:checked')) !== null && _b !== void 0 ? _b : []).map(function (checkbox) { return checkbox.value; });
        var hasOtherFilter = ((_c = filtersRoot === null || filtersRoot === void 0 ? void 0 : filtersRoot.querySelectorAll('input[name=category]:checked, input[name=color]:checked').length) !== null && _c !== void 0 ? _c : 0) > 0
            || rangeFilters.some(function (filter) { return filter.isActive(); });
        var isEmpty = q === '' && checkedGroups.length === 0 && !hasOtherFilter;
        // Only the standalone category-group page (category.html, where
        // #page-intro-title is server-rendered -> startedOnCategoryPage) keeps
        // the group -> sub-category-tiles drill-down. On plain /search,
        // selecting one Category behaves like any other filter and shows the
        // results grid directly. sub-category.html also sets
        // startedOnCategoryPage, but it presets category[] so hasOtherFilter
        // stays true there -- this branch isn't reached while that filter is
        // set (and if the shopper clears it, showing the group's tiles
        // matches what a reload of the canonicalised /{lang}/{group} URL does
        // anyway). See
        // docs/superpowers/specs/2026-08-29-search-category-goes-straight-to-results-design.md.
        var selectedGroup = startedOnCategoryPage
            && q === '' && checkedGroups.length === 1 && !hasOtherFilter
            ? checkedGroups[0]
            : null;
        if (categoryBrowse) {
            categoryBrowse.hidden = !isEmpty;
        }
        categoryBrowseGroups.forEach(function (el) {
            el.hidden = el.dataset.group !== selectedGroup;
        });
        if (resultsSection) {
            resultsSection.hidden = isEmpty || selectedGroup !== null;
        }
    }
    function renderActiveState() {
        updateChipCounts();
        updateResetVisibility();
        updateBrowseState();
    }
    /**
     * Applies SearchResultsApi::resolvePageChrome()'s title/meta_description/
     * breadcrumb_html for the just-fetched state -- document.title always
     * carries the "Pierre Bex - " prefix base.html's own <title> block does
     * (data.page.title is just the page-specific suffix, matching $title
     * everywhere else in this codebase).
     */
    function updatePageChrome(page) {
        var _a;
        // Retitling the tab to "Brooches" (and swapping the breadcrumb to
        // match), from plain /search, just because exactly one category/
        // sub-category happens to be checked, would claim a navigation to
        // that category page that never actually happened -- checking a
        // category/sub-category filter from /search only ever updates the
        // filters/results/URL, same as any other filter, unless that's
        // genuinely where this page started (see startedOnCategoryPage
        // above), where the title/breadcrumb tracking the active
        // group/sub-category is the whole point (e.g. unchecking the
        // sub-category chip resolves the heading back up to its own group).
        if (startedOnCategoryPage) {
            document.title = "Pierre Bex - ".concat(page.title);
            var breadcrumbEl = document.querySelector('.categories-overview.breadcrumb');
            if (breadcrumbEl) {
                breadcrumbEl.outerHTML = page.breadcrumb_html.trim();
            }
        }
        (_a = document.querySelector('meta[name="description"]')) === null || _a === void 0 ? void 0 : _a.setAttribute('content', page.meta_description);
        // The visible on-page heading (category.html/sub-category.html's own
        // page-intro block, see search.html) only exists on a category/
        // sub-category page -- absent on plain /search, so this is a no-op
        // there. Its own intro paragraph stays as originally server-rendered
        // (still accurate: generic enough to apply regardless of which
        // category/group ends up selected), only the heading text tracks
        // page.title, same as document.title/the breadcrumb above.
        var pageIntroTitleEl = document.getElementById('page-intro-title');
        if (pageIntroTitleEl) {
            pageIntroTitleEl.textContent = page.title;
        }
    }
    function renderResults(data, append) {
        if (!append) {
            grid.innerHTML = '';
        }
        data.products.forEach(function (product) {
            var el = renderProduct(product);
            if (el) {
                grid.appendChild(el);
            }
        });
        total = data.total;
        offset = append ? offset + data.products.length : data.products.length;
        resultCountEl.textContent = data.result_count_html;
        loadMoreContainer.style.display = offset < total ? '' : 'none';
        if (!append) {
            renderFacets(data.facets);
            updatePageChrome(data.page);
            renderActiveState();
        }
    }
    function handleFetchError(error) {
        // Same convention as umamiTrack()/etc. in index.ts: report to Sentry so
        // a production fetch failure actually surfaces in error monitoring
        // instead of only a browser devtools console nobody has open.
        if (Sentry) {
            Sentry.captureException(error);
        }
        else {
            console.error(error);
        }
    }
    // Guards append (offset-based "next page") requests specifically, not
    // filter/sort replacements: offset only advances once a response lands
    // (see renderResults), so a second append request firing before the
    // first resolves -- e.g. the scroll sentinel re-entering the viewport
    // repeatedly during one scroll gesture -- would read the same stale
    // offset twice, fetch overlapping pages, and then double-advance offset
    // once both responses land, silently skipping the products in between.
    var isFetchingMore = false;
    /**
     * The inverse of canonicalUrlPath() -- reads the *current* URL (used on
     * popstate) back into the same group[]/category[] query-param shape
     * buildParams()/syncFiltersFromParams() already work with, whether it's
     * a plain /{lang}/search?... URL or a canonical /{lang}/{group}
     * [/{category}] one a shopper navigated back/forward to. Without this,
     * landing back on e.g. /{lang}/brooches (no query string at all) would
     * read as "no filters" and clear every checkbox instead of re-checking
     * Category: Brooches.
     */
    function paramsFromCurrentLocation() {
        var params = new URLSearchParams(window.location.search);
        var segments = window.location.pathname.split('/').filter(function (segment) { return segment !== ''; });
        if (segments.length >= 2 && CATEGORY_GROUPS.indexOf(segments[1]) !== -1) {
            if (segments.length >= 3) {
                params.set('category[]', segments[2]);
            }
            else {
                params.set('group[]', segments[1]);
            }
        }
        return params;
    }
    /**
     * The canonical URL for a filter state that's really "just this one
     * category/group" -- category.html/sub-category.html render this exact
     * same content at their own dedicated URL. Only ever consulted while
     * startedOnCategoryPage is true (see its own call site in
     * fetchAndRender()) -- from plain /search, checking the same Category
     * chip stays on /search?group[]=brooches instead, since no navigation
     * to that category page was ever requested there. Colour, price, and a
     * non-default sort don't disqualify this -- they stay layered on as an
     * ordinary query string on top of the category/group path (e.g.
     * /{lang}/brooches/art-deco-brooches?color[]=gold), since
     * CategoryGroupDocument/ItemCategoryDocument read $_GET for exactly
     * these dimensions on top of their own fixed group/category preset.
     * Returns null for every other state, including an actual text query
     * (there's no `q=` param on a category URL).
     */
    function canonicalUrlPath() {
        var _a, _b, _c, _d;
        var q = (_a = qInput === null || qInput === void 0 ? void 0 : qInput.value.trim()) !== null && _a !== void 0 ? _a : '';
        if (q !== '') {
            return null;
        }
        var checkedCategories = Array.from((_b = filtersRoot === null || filtersRoot === void 0 ? void 0 : filtersRoot.querySelectorAll('input[name=category]:checked')) !== null && _b !== void 0 ? _b : []);
        var checkedGroups = Array.from((_c = filtersRoot === null || filtersRoot === void 0 ? void 0 : filtersRoot.querySelectorAll('input[name=group]:checked')) !== null && _c !== void 0 ? _c : []).map(function (checkbox) { return checkbox.value; });
        var path = null;
        if (checkedCategories.length === 1) {
            var group = (_d = checkedCategories[0].closest('.filter-panel-group')) === null || _d === void 0 ? void 0 : _d.dataset.group;
            if (group && (checkedGroups.length === 0 || (checkedGroups.length === 1 && checkedGroups[0] === group))) {
                // An explicit group[] is fine alongside category[] as long as it
                // agrees with the category's own real group -- otherwise this
                // isn't really "just this category alone" (a contradictory
                // combination matching zero products), so path stays null.
                path = "/".concat(lang, "/").concat(group, "/").concat(checkedCategories[0].value);
            }
        }
        else if (checkedCategories.length === 0 && checkedGroups.length === 1) {
            path = "/".concat(lang, "/").concat(checkedGroups[0]);
        }
        if (path === null) {
            return null;
        }
        // group[]/category[] are already baked into the path above -- every
        // other dimension buildParams() would otherwise put in the query
        // string (colour, price, sort) rides along on top of it instead.
        var extraParams = buildParams();
        extraParams.delete('group[]');
        extraParams.delete('category[]');
        var query = extraParams.toString();
        return query === '' ? path : "".concat(path, "?").concat(query);
    }
    function fetchAndRender(append, pushUrl, onRendered) {
        if (append) {
            if (isFetchingMore) {
                return;
            }
            isFetchingMore = true;
        }
        var params = buildParams();
        if (append) {
            params.set('offset', String(offset));
        }
        if (pushUrl) {
            // Only resolves to a category/group's own canonical URL (and, via
            // updatePageChrome() above, its title/breadcrumb) when the page
            // actually started as one (see startedOnCategoryPage) -- picking a
            // Category/Sub-category chip from plain /search is just another
            // filter like any other there, narrowing results in place without
            // claiming a navigation to that category page that never happened.
            var canonicalPath = startedOnCategoryPage ? canonicalUrlPath() : null;
            var url = canonicalPath !== null && canonicalPath !== void 0 ? canonicalPath : "/".concat(lang, "/search?").concat(params.toString());
            history.pushState(null, '', url);
        }
        fetch("".concat(searchApiUrl, "?").concat(params.toString()))
            .then(function (response) { return response.json(); })
            .then(function (data) {
            renderResults(data, append);
            if (append) {
                isFetchingMore = false;
            }
            else if (onRendered) {
                onRendered(data);
            }
        })
            .catch(function (error) {
            if (append) {
                isFetchingMore = false;
            }
            handleFetchError(error);
        });
    }
    filtersRoot === null || filtersRoot === void 0 ? void 0 : filtersRoot.addEventListener('change', function (evt) {
        var target = evt.target;
        if (target.tagName !== 'INPUT') {
            return;
        }
        var input = target;
        if (input.name === 'group') {
            updateSubcategoryVisibility();
        }
        var partial;
        if (input === soldOutToggle) {
            partial = { dimension: 'sold_out', action: 'set', value: input.checked ? 'shown' : 'hidden' };
        }
        else if (['group', 'category', 'color'].indexOf(input.name) !== -1) {
            partial = { dimension: input.name, action: input.checked ? 'add' : 'remove', value: input.value };
        }
        else if (input.id.startsWith('price-')) {
            var active = priceFilter.isActive();
            partial = { dimension: 'price', action: active ? 'set' : 'clear', value: active ? priceFilter.currentValue() : undefined };
        }
        else if (input.id.startsWith('size-')) {
            var active = sizeFilter.isActive();
            partial = { dimension: 'size', action: active ? 'set' : 'clear', value: active ? sizeFilter.currentValue() : undefined };
        }
        else {
            // Defensive: no current #filter-bar input falls here. A non-filter
            // <input> added to #filter-bar later would auto-fetch + emit a noisy
            // 'unknown' row -- classify it above instead.
            partial = { dimension: 'unknown', action: 'set' };
        }
        applyFilterChange(partial);
    });
    // The browse tiles' own <a href> (see search.html's own comment) is a
    // real navigation only on category.html itself -- startedOnCategoryPage
    // is the same signal fetchAndRender() already uses for the equivalent
    // chip-driven decision, since #filter-bar's own checkboxes stay in the
    // DOM there too (just [hidden], see style.css), so their mere
    // existence can't tell the two contexts apart. On plain /search,
    // clicking a tile is just another way to check the same
    // Category/Sub-category checkbox a chip would, so it's intercepted the
    // same way any other filter change is -- in place, no navigation, same
    // URL shape.
    if (!startedOnCategoryPage) {
        categoryBrowse === null || categoryBrowse === void 0 ? void 0 : categoryBrowse.addEventListener('click', function (evt) {
            var tile = evt.target.closest('a[data-group]');
            var group = tile === null || tile === void 0 ? void 0 : tile.getAttribute('data-group');
            var checkbox = group
                ? filtersRoot === null || filtersRoot === void 0 ? void 0 : filtersRoot.querySelector("input[name=group][value=\"".concat(CSS.escape(group), "\"]"))
                : null;
            if (!checkbox) {
                return;
            }
            evt.preventDefault();
            checkbox.checked = true;
            updateSubcategoryVisibility();
            applyFilterChange({ dimension: 'group', action: 'add', value: group !== null && group !== void 0 ? group : undefined });
        });
    }
    resetButton === null || resetButton === void 0 ? void 0 : resetButton.addEventListener('click', function () {
        resetAllFilterControls();
        applyFilterChange({ dimension: 'all', action: 'reset_all' });
    });
    document.querySelectorAll('.filter-panel-reset').forEach(function (button) {
        var _a;
        // data-filter-dimension is authored per panel in search.html as one of
        // the FilterDimension slugs.
        var dimension = ((_a = button.dataset.filterDimension) !== null && _a !== void 0 ? _a : 'unknown');
        button.addEventListener('click', function (evt) {
            // Reset without closing the dropdown or triggering the chip
            // button's own toggle/close-all-panels click handler.
            evt.stopPropagation();
            resetDimension(dimension);
        });
    });
    sortSelect === null || sortSelect === void 0 ? void 0 : sortSelect.addEventListener('change', function () { return applyFilterChange({
        dimension: 'sort', action: 'set', value: sortSelect.value,
    }); });
    if (viewStyleRange) {
        // localStorage.getItem() returns null, not undefined, when unset --
        // Number(null) is 0 (a valid index!), not NaN, so this must check for
        // null explicitly rather than fall through Number.isInteger() alone,
        // or "no stored preference" would silently resolve to index 0
        // ('list') instead of the intended default below (1, 'grid-small').
        var storedRaw = localStorage.getItem(VIEW_STYLE_STORAGE_KEY);
        var stored = storedRaw === null ? NaN : Number(storedRaw);
        var initialIndex = Number.isInteger(stored) && stored >= 0 && stored < VIEW_STYLES.length ? stored : 1;
        viewStyleRange.value = String(initialIndex);
        applyViewStyle(initialIndex);
        viewStyleRange.addEventListener('input', function () { return applyViewStyle(Number(viewStyleRange.value)); });
    }
    viewStyleLabels.forEach(function (label) {
        label.addEventListener('click', function () {
            var index = Number(label.dataset.value);
            if (viewStyleRange) {
                viewStyleRange.value = String(index);
            }
            applyViewStyle(index);
        });
    });
    searchForm === null || searchForm === void 0 ? void 0 : searchForm.addEventListener('submit', function (evt) {
        var _a;
        evt.preventDefault();
        // A typed query starts a fresh search rather than narrowing whatever
        // filters happened to still be checked from browsing before it --
        // matches the no-JS fallback (see _search-box.html, which no longer
        // carries $activeFilters forward as hidden fields either).
        // resetAllFilterControls() sets .checked = false programmatically, so
        // no 'change' events fire and this filter-clearing is deliberately
        // NOT recorded as a Filter Change -- it's attributed to the new
        // Search event below (see the design spec's Known Limitations).
        resetAllFilterControls();
        var query = (_a = qInput === null || qInput === void 0 ? void 0 : qInput.value.trim()) !== null && _a !== void 0 ? _a : '';
        // Not gated by analyticsReady (unlike emitFilterChange): this handler
        // is registered inside runSearchResults() and cannot fire before the
        // function returns and sets the flag.
        fetchAndRender(false, true, function (data) {
            if (query !== '') {
                emitSearch(query, data.total, 'refine');
            }
        });
    });
    loadMoreButton === null || loadMoreButton === void 0 ? void 0 : loadMoreButton.addEventListener('click', function () { return fetchAndRender(true, false); });
    var sentinel = document.getElementById('search-scroll-sentinel');
    if (sentinel && 'IntersectionObserver' in window) {
        var observer = new IntersectionObserver(function (entries) {
            if (entries[0].isIntersecting && offset < total) {
                fetchAndRender(true, false);
            }
        }, 
        // Start fetching the next page while the sentinel is still below the
        // fold, not only once it's fully visible, so results are ready by
        // the time the shopper actually scrolls that far.
        { rootMargin: '400px 0px' });
        observer.observe(sentinel);
    }
    // Chip open/close: one panel open at a time, closed by clicking its own
    // chip again, clicking outside every panel/chip, or Escape.
    var chipGroups = Array.from(document.querySelectorAll('.filter-chip-group'));
    function closeAllPanels() {
        chipGroups.forEach(function (chipGroup) {
            var button = chipGroup.querySelector('.filter-chip');
            var panel = chipGroup.querySelector('.filter-panel');
            if (panel) {
                panel.hidden = true;
            }
            if (button) {
                button.setAttribute('aria-expanded', 'false');
            }
        });
    }
    /**
     * A fixed CSS left/right rule can't keep every chip's panel on-screen at
     * once -- a chip near the left edge needs the opposite correction from
     * one near the right, especially on mobile where a 220px+ panel is a
     * big fraction of the viewport. Measured and clamped in JS instead,
     * every time a panel opens.
     */
    function clampPanelToViewport(panel) {
        var margin = 8;
        panel.style.left = '0';
        var rect = panel.getBoundingClientRect();
        var shift = 0;
        if (rect.right > window.innerWidth - margin) {
            shift = window.innerWidth - margin - rect.right;
        }
        // Prefer keeping the left edge on-screen over the right if the panel
        // is wider than the viewport has room for either way.
        if (rect.left + shift < margin) {
            shift = margin - rect.left;
        }
        panel.style.left = "".concat(shift, "px");
    }
    chipGroups.forEach(function (chipGroup) {
        var button = chipGroup.querySelector('.filter-chip');
        var panel = chipGroup.querySelector('.filter-panel');
        button === null || button === void 0 ? void 0 : button.addEventListener('click', function (evt) {
            evt.stopPropagation();
            var wasOpen = panel ? !panel.hidden : false;
            closeAllPanels();
            if (panel && !wasOpen) {
                panel.hidden = false;
                button.setAttribute('aria-expanded', 'true');
                clampPanelToViewport(panel);
            }
        });
        panel === null || panel === void 0 ? void 0 : panel.addEventListener('click', function (evt) { return evt.stopPropagation(); });
    });
    document.addEventListener('click', function () { return closeAllPanels(); });
    document.addEventListener('keydown', function (evt) {
        if (evt.key === 'Escape') {
            closeAllPanels();
        }
    });
    window.addEventListener('popstate', function () {
        var params = paramsFromCurrentLocation();
        syncFiltersFromParams(params);
        fetch("".concat(searchApiUrl, "?").concat(params.toString()))
            .then(function (response) { return response.json(); })
            .then(function (data) { return renderResults(data, false); })
            .catch(handleFetchError);
    });
    updateSubcategoryVisibility();
    renderActiveState();
    setSearchFilterContext(function () { return compact(__assign({}, buildFilterSnapshot())); });
    // Click-through to an item page from a result card (a.caption is the
    // card's title/price link; a.zoom opens the lightbox instead and is
    // handled by the Fancybox path). Fired before navigation -- Umami's
    // track() uses navigator.sendBeacon, which survives unload.
    grid.addEventListener('click', function (evt) {
        var link = evt.target.closest('a.caption');
        if (link && grid.contains(link)) {
            emitSearchResultEngagement('click', link);
        }
    });
    analyticsReady = true;
}
